// All fields optional: employer entries are drafts, and the wizard's
// StepDataStore types them as loose EmploymentEntry records — requiring
// fields here made the two stores mutually unassignable.
export type IncomeEmployerDraft = {
  [key: string]: unknown;
  id?: string;
  employerName?: string;
  employerPhone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  jobTitle?: string;
  employmentType?: 'Full-Time' | 'Part-Time' | 'Self-Employed' | 'Contract' | 'Other' | '';
  startDate?: string;
  endDate?: string;
  currentlyEmployed?: boolean;
  monthlyGrossIncome?: string;
  businessName?: string;
  ownershipPercentage?: string;
  naicsCode?: string;
  businessStartDate?: string;
  isPrimary?: boolean;
};

export type IncomeDocumentDraft = {
  id: string;
  kind: 'paystub' | 'w2' | 'tax-return';
  name: string;
  size: number;
  uploadedAt: string;
};

// The index signature must be `unknown`, not Record<string, unknown>: an
// intersection with Record<string, Record<string, unknown> | undefined> made
// every object-literal write with a scalar field (lastStepKey, loanId, …)
// unassignable. Known step keys are named below so reads keep object typing.
export type ApplicationDraft = {
  [key: string]: unknown;
  /** Browser-local owner marker for cross-account draft isolation. */
  draftOwnerKey?: string;
  loanId?: string;
  /** NP-424: set by "Start a New Loan" — tells the wizard save flow to CREATE
      rather than resume the borrower's existing in-progress application. */
  newLoanIntent?: boolean;
  /** NP-588: the loan was created while the borrower LEFT the step (a page-selector
      jump or Save & exit), so the per-tab PATCHes never ran and submit must replay
      them. Bookkeeping, not borrower work — see the content filters below. */
  replayTabsBeforeSubmit?: boolean;
  eConsentSigned?: boolean;
  lastSavedAt?: string;
  lastStepKey?: string;
  furthestStepKey?: string;
  // WS9-1 Phase 3 — fast-path submit markers written after a successful
  // POST /application/{loanId}/submit (display cache only; server is truth).
  applicationStatus?: string;
  submittedAt?: string;
  loanNumber?: string;
  personal?: Record<string, unknown>;
  property?: Record<string, unknown>;
  declarations?: Record<string, unknown>;
  demographics?: Record<string, unknown>;
  disclosures?: Record<string, unknown>;
  acknowledgements?: Record<string, boolean>;
  liabilities?: Record<string, unknown>;
  review?: Record<string, unknown>;
  assets?: Array<Record<string, unknown>>;
  residency?: {
    currentAddress?: string;
    yearsAtAddress?: string;
    previousAddress?: string;
    previousAddresses?: Array<Record<string, unknown>>;
    livedMoreThan2Years?: string;
    [key: string]: unknown;
  };
  coBorrowerResidency?: {
    currentAddress?: string;
    yearsAtAddress?: string;
    previousAddress?: string;
    previousAddresses?: Array<Record<string, unknown>>;
    livedMoreThan2Years?: string;
    [key: string]: unknown;
  };
  military?: {
    veteranStatus?: string;
    branch?: string;
    serviceDates?: string;
    [key: string]: unknown;
  };
  income?: Record<string, unknown> & {
    employers?: IncomeEmployerDraft[];
    gapExplanation?: string;
    documents?: IncomeDocumentDraft[];
    warning?: string;
    completed?: boolean;
    lastSavedAt?: string;
  };
};

const STORAGE_KEY = 'path-application-draft';

function normalizeOwnerKey(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

function readOwnerFromStorage(storage: Storage): string {
  try {
    const raw = storage.getItem('path_current_user');
    if (!raw) return '';
    const parsed = JSON.parse(raw) as { email?: unknown };
    return typeof parsed.email === 'string' ? normalizeOwnerKey(parsed.email) : '';
  } catch {
    return '';
  }
}

export function getCurrentSessionOwnerKey(): string {
  if (typeof window === 'undefined') return '';
  return readOwnerFromStorage(window.localStorage) || readOwnerFromStorage(window.sessionStorage);
}

/** Which loan, which owner, how far along — everything else is borrower-entered work. */
const DRAFT_BOOKKEEPING_KEYS = [
  'draftOwnerKey',
  'loanId',
  'lastSavedAt',
  'lastStepKey',
  'furthestStepKey',
  'newLoanIntent',
  'replayTabsBeforeSubmit',
];

export function hasMeaningfulDraftForOwner(
  draft: ApplicationDraft | null | undefined,
  ownerKey?: string | null,
): boolean {
  if (!draft || typeof draft !== 'object') return false;

  const expectedOwner = normalizeOwnerKey(ownerKey ?? getCurrentSessionOwnerKey());
  const draftOwner = normalizeOwnerKey(typeof draft.draftOwnerKey === 'string' ? draft.draftOwnerKey : '');
  if (!expectedOwner || !draftOwner || draftOwner !== expectedOwner) return false;

  return Object.keys(draft).some((k) => !DRAFT_BOOKKEEPING_KEYS.includes(k));
}

/**
 * Everything in a draft that still applies once `loanId` is the loan on screen.
 *
 * `path-application-draft` and `path_application_progress` are per-owner, so a
 * loan switch leaves the previous application's answers in place — nothing on
 * that path clears them. Hydration backfills every field the server is silent
 * on (NP-654), and on a freshly created loan that is most of them, so the old
 * answers land on the new loan and the next persist restamps them with its id.
 *
 * A draft naming a DIFFERENT loan keeps only what is not about that loan: who
 * owns it, and a pending "Start a New Loan". Its step content, its id and its
 * step positions all describe the application the borrower left. No `loanId` at
 * all means the application has not been created yet — that is this borrower's
 * current typing and it stays untouched.
 */
const CROSS_LOAN_SURVIVING_KEYS = ['draftOwnerKey', 'newLoanIntent'];

export function draftForLoan<T extends Record<string, unknown>>(draft: T, loanId: string | undefined): Partial<T> {
  if (!draft || typeof draft !== 'object') return draft;
  const draftLoanId = typeof draft.loanId === 'string' ? draft.loanId : undefined;
  if (!draftLoanId || !loanId || draftLoanId === loanId) return draft;

  return Object.fromEntries(
    Object.entries(draft).filter(([key]) => CROSS_LOAN_SURVIVING_KEYS.includes(key)),
  ) as Partial<T>;
}

/**
 * NP-445: the declarations slot is per-borrower — `{ borrower, coBorrower }` —
 * matching the shape `demographics` already uses and that `stepValidation` and
 * `ApplicationShell` already read (`declarations.borrower ?? declarations`).
 *
 * Drafts written before NP-445 hold the borrower's fields FLAT at the top level.
 * Those keep working: a flat draft resolves to the borrower and yields nothing
 * for the co-borrower. Same back-compat stance as the income step's
 * `borrowerEmployments ?? employers` fallback.
 *
 * Every reader must go through this so no call site re-invents the fallback.
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function selectPerBorrowerSection(
  section: Record<string, unknown> | undefined,
  who: 'borrower' | 'coBorrower',
): Record<string, unknown> {
  // localStorage is user-writable and can hold anything (a string, null, an
  // array) — normalise before touching it, or `in` throws a TypeError and takes
  // the whole step down.
  if (!isPlainObject(section)) return {};
  const root = section;

  const nested = root[who];
  if (isPlainObject(nested)) return nested;

  // Legacy flat draft: the fields belong to the primary borrower only.
  const hasSlot =
    Object.prototype.hasOwnProperty.call(root, 'borrower') ||
    Object.prototype.hasOwnProperty.call(root, 'coBorrower');
  if (who === 'borrower' && !hasSlot) return root;
  return {};
}

export function selectDeclarationsFor(
  declarations: Record<string, unknown> | undefined,
  who: 'borrower' | 'coBorrower',
): Record<string, unknown> {
  return selectPerBorrowerSection(declarations, who);
}

/**
 * NP-626 AC-13: demographics carries the same `{ borrower, coBorrower }` shape,
 * and now needs the same role-scoped read — the save gate validates only the
 * signed-in borrower's own section. Its readers used to spell the fallback
 * inline as `demographics.borrower ?? demographics`, which handed a root holding
 * only a `coBorrower` slot back as if it were the borrower's answers.
 */
export function selectDemographicsFor(
  demographics: Record<string, unknown> | undefined,
  who: 'borrower' | 'coBorrower',
): Record<string, unknown> {
  return selectPerBorrowerSection(demographics, who);
}

export function readApplicationDraft(): ApplicationDraft {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) as ApplicationDraft : {};
  } catch {
    return {};
  }
}

export function writeApplicationDraft(draft: ApplicationDraft) {
  if (typeof window === 'undefined') return;
  const ownerKey = getCurrentSessionOwnerKey();
  window.localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      ...draft,
      ...(ownerKey ? { draftOwnerKey: ownerKey } : {}),
      lastSavedAt: new Date().toISOString(),
    }),
  );
}

/**
 * The draft a save builds on. `writeApplicationDraft` stamps the signed-in owner onto
 * whatever it is given, so merging into a draft another account left behind would
 * relabel that account's application — submit markers included — as this one's, and
 * every owner check downstream would then accept it (NP-715: a borrower with no loan
 * landed on "being prepared for disclosures"). Another owner's draft is not a base.
 * A draft with no owner stays adoptable, matching LoanContext's NP-529 rule.
 */
function readDraftToExtend(): ApplicationDraft {
  const current: unknown = readApplicationDraft();
  // A corrupt stored value (`null`, a primitive) must not break every later save.
  if (!isPlainObject(current)) return {};
  const draftOwner = normalizeOwnerKey(typeof current.draftOwnerKey === 'string' ? current.draftOwnerKey : '');
  const sessionOwner = getCurrentSessionOwnerKey();
  const ownedByAnotherAccount = Boolean(draftOwner && sessionOwner && draftOwner !== sessionOwner);
  return ownedByAnotherAccount ? {} : current;
}

export function mergeApplicationDraft(partial: Partial<ApplicationDraft>) {
  const current = readDraftToExtend();
  const next = { ...current, ...partial } satisfies ApplicationDraft;
  writeApplicationDraft(next);
  return next;
}

export function updateApplicationDraft(updater: (draft: ApplicationDraft) => ApplicationDraft) {
  const next = updater(readDraftToExtend());
  writeApplicationDraft(next);
  return next;
}

/**
 * BUG-11 fix: Draft lifecycle management — clear ALL application-owned localStorage
 * keys on logout so a subsequent user session starts completely clean.
 *
 * Keys cleared:
 *  - path-application-draft      (applicationDraft.ts)
 *  - path_application_progress   (ApplicationShell.tsx saveProgressToLocalStorage)
 *  - path-submitted-application   (RETIRED — WS9-1 Phase 3; still purged so
 *                                  old clients' stale snapshots are cleaned up)
 *
 * Keys preserved (deliberately NOT in the sweep):
 *  - path_lo_email               (NP-415 — LO persists across applications)
 *
 * The MockAuthProvider's _clearSession() already sweeps all path_ / path- keys;
 * this function makes the same cleanup available to EVERY auth provider (including
 * RealAuthProvider) through the AuthContext logout hook so no provider can miss it.
 */
const DRAFT_LIFECYCLE_KEYS = [
  'path-application-draft',
  'path_application_progress',
  'path-submitted-application',
  // NP-463: the saved-baseline snapshot is draft data — it must not outlive the
  // draft it describes, or the next borrower's Discard could restore this one's
  // values.
  'path-application-draft-saved',
  // NOTE (NP-415): 'path_lo_email' is intentionally NOT cleared here. The LO
  // relationship outlives a single application — clearing it on draft-clear
  // (submit cleanup, "Start a New Loan", logout) was exactly why subsequent
  // loans lost the LO assignment. A different ?lo= URL still overrides via
  // LOEmailCapture (new LO takes precedence, NP-415 AC4).
  // NOTE (RCA 2026-06-19 rev2): 'path-active-loan-id' is intentionally NOT cleared on
  // logout.  Clearing it caused a "Purchase/Refinance re-selection" flash on every login:
  //   1. User logs out → activeLoanId cleared
  //   2. User logs back in → activeLoanId = undefined
  //   3. useIncompleteApplications race: app marks user as "fresh" if incomplete-apps
  //      query fails/times out on cold Azure container start
  //   4. Dashboard shows Purchase/Refinance buttons even though a draft loan exists
  // Keeping the ID across logout lets LoanContext reload the correct loan instantly
  // on next login.  If the ID is stale (DB reset, GUID drift), the 404 handler in
  // LoanContext clears it and falls back to useIncompleteApplications auto-select.
  // NOTE (Task 3 fix rev2): 'sophia-panel-collapsed' is intentionally NOT listed here.
  // The key has been migrated from localStorage to sessionStorage in SophiaChat.tsx so it
  // automatically resets on every hard refresh / new tab without any logout required.
  // Listing it here would be a no-op (localStorage.removeItem on a sessionStorage key)
  // and could mask future regressions if the storage is ever switched back.
] as const;

/**
 * NP-430 — draft retention across logout, scoped to the borrower.
 *
 * The wizard cannot create the loan until the borrower's basics exist
 * (POST /api/v1/application requires first name, last name, email AND a home
 * phone — registration collects no phone). A borrower who registers and goes
 * straight to Income & Assets therefore has NOWHERE to persist that work
 * server-side, and logout used to delete the local draft outright: every
 * employment, income and asset row vanished on re-login.
 *
 * Logout now stashes the draft under a key scoped to that borrower and login
 * restores it, so the same person on the same device gets their work back. A
 * DIFFERENT borrower signing in never sees it, and submit / "Start a New Loan"
 * clear the stash along with the draft. (Same spirit as `path_lo_email` and
 * `path-active-loan-id`, which already outlive a logout by design.)
 *
 * Cross-device persistence still requires Personal Info — that is an API
 * requirement, not something the client can work around.
 */
const DRAFT_STASH_PREFIX = 'path-application-draft:owner:';

function stashKey(ownerKey: string): string {
  return `${DRAFT_STASH_PREFIX}${ownerKey.trim().toLowerCase()}`;
}

/** True when the draft holds borrower-entered work worth preserving. */
function draftHasContent(raw: string | null): boolean {
  if (!raw) return false;
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return Object.keys(parsed).some(
      (k) => !['loanId', 'lastSavedAt', 'lastStepKey', 'furthestStepKey', 'newLoanIntent', 'replayTabsBeforeSubmit'].includes(k),
    );
  } catch {
    return false;
  }
}

/** Copy the live draft into the borrower's stash. Call BEFORE clearing on logout. */
export function stashApplicationDraft(ownerKey: string | null | undefined): void {
  if (typeof window === 'undefined' || !ownerKey) return;
  try {
    const raw = window.localStorage.getItem('path-application-draft');
    if (!draftHasContent(raw)) return;
    window.localStorage.setItem(stashKey(ownerKey), raw!);
  } catch {
    /* storage restricted — retention is best-effort */
  }
}

/**
 * Restore the borrower's stashed draft when they sign back in and have no live
 * draft. Returns true when something was restored.
 */
export function restoreApplicationDraft(ownerKey: string | null | undefined): boolean {
  if (typeof window === 'undefined' || !ownerKey) return false;
  try {
    const stashed = window.localStorage.getItem(stashKey(ownerKey));
    if (!draftHasContent(stashed)) return false;
    // Never clobber work in progress — only fill an empty draft.
    if (draftHasContent(window.localStorage.getItem('path-application-draft'))) return false;
    window.localStorage.setItem('path-application-draft', stashed!);
    window.localStorage.removeItem(stashKey(ownerKey));
    // NP-463: what we just restored is the borrower's own saved work, so it is
    // the baseline a later Discard reverts to. Without this the restored draft
    // would look entirely unsaved and a Discard would delete it.
    markDraftSaved();
    return true;
  } catch {
    return false;
  }
}

/** Drop a borrower's stash (submit cleanup / "Start a New Loan"). */
export function clearApplicationDraftStash(ownerKey?: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (ownerKey) {
      window.localStorage.removeItem(stashKey(ownerKey));
      return;
    }
    // No owner in hand: drop every stash (submit / new-application cleanup).
    for (let i = window.localStorage.length - 1; i >= 0; i -= 1) {
      const key = window.localStorage.key(i);
      if (key?.startsWith(DRAFT_STASH_PREFIX)) window.localStorage.removeItem(key);
    }
  } catch {
    /* ignore */
  }
}

export function clearApplicationDraft(): void {
  if (typeof window === 'undefined') return;
  DRAFT_LIFECYCLE_KEYS.forEach((key) => {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // Ignore — storage may be restricted in some browser privacy modes
    }
  });
}

/**
 * NP-469 — the wizard step slots a Discard must drop.
 *
 * ALLOW-LIST on purpose. Every key here holds data the borrower typed into a
 * step; everything NOT listed is bookkeeping that must survive a discard
 * (`loanId` and `newLoanIntent` decide which loan the next visit resumes,
 * `eConsentSigned` would otherwise re-gate e-consent, and the submit markers
 * describe the server's state, not the borrower's edits). A deny-list would
 * silently wipe any slot added later.
 */
const WIZARD_STEP_DATA_KEYS = [
  'personal',
  'residency',
  'coBorrowerResidency',
  'income',
  'assets',
  'property',
  'liabilities',
  'declarations',
  'military',
  'demographics',
  'disclosures',
  'acknowledgements',
  'review',
] as const;

/**
 * Discard the borrower's UNSAVED wizard edits (NP-469).
 *
 * The wizard persists as the user types, so "unsaved" data is already on disk —
 * a Discard that only reset component state left it there to be rehydrated on
 * the next visit.
 *
 * THE BOUNDARY: this drops the LOCAL step data only. It never touches the
 * server, so the next visit rehydrates whatever the borrower genuinely saved
 * (`GET /application/{loanId}` → mapLoanResponseToStepData) and loses only what
 * never got there. That is why no row-level "was this saved?" test is needed,
 * and why a discard cannot destroy saved data.
 *
 * Callers MUST also clear the wizard's in-memory step data — `persistDraft`
 * spreads `stepDataRef.current`, so the rAF flush or the 2s autosave would
 * write every row straight back otherwise.
 */
/**
 * Snapshot of the step slots as of the borrower's last completed save — the
 * baseline a Discard reverts to (NP-463).
 */
const SAVED_BASELINE_KEY = 'path-application-draft-saved';

/**
 * Record "this is what the borrower has saved" (NP-463).
 *
 * Called after every completed step save, including one where the Vesta create
 * was deferred or failed: the borrower pressed Save and was told "Progress
 * saved", and per NP-424 the draft is deliberately the only copy until Personal
 * Info supplies the basics the create needs. Treating that as unsaved is exactly
 * what made a later Discard unrecoverable.
 */
export function markDraftSaved(): void {
  if (typeof window === 'undefined') return;
  try {
    const draft = readApplicationDraft();
    const baseline: Record<string, unknown> = {};
    for (const key of WIZARD_STEP_DATA_KEYS) {
      if (draft[key] !== undefined) baseline[key] = draft[key];
    }
    window.localStorage.setItem(SAVED_BASELINE_KEY, JSON.stringify(baseline));
  } catch {
    // Storage unavailable — a discard then falls back to clearing, which is the
    // pre-NP-463 behaviour rather than a new failure mode.
  }
}

/**
 * The step slots as of the borrower's last completed save. Exported so callers
 * that must distinguish SAVED data from work in progress have a signal the
 * keystroke-by-keystroke draft cannot give them (NP-573).
 */
export function readSavedBaseline(): Record<string, unknown> | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(SAVED_BASELINE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

export function discardUnsavedStepData(): void {
  if (typeof window === 'undefined') return;
  // NP-463: REVERT to the last saved baseline rather than deleting the slots.
  //
  // NP-469 deleted them on the assumption that the server held a complete copy
  // of anything saved. It does not, for two reasons, and QA hit both:
  //   1. Personal Info is the step that CREATES the loan, so until basics are
  //      complete there is no server copy at all (NP-424 defers the create) —
  //      deleting the draft lost the data with nothing to restore from;
  //   2. even with a loan, mapLoanResponseToStepData reads back only a SUBSET of
  //      what buildPersonalTabDelta writes (militaryService, preferredLanguage,
  //      the domestic-relationship answers and hasCoBorrower are sent and never
  //      returned), so the round-trip silently dropped them.
  // Reverting to the baseline makes the dialog's promise — keep only what you
  // have saved — literally true, with no dependence on that round-trip.
  const baseline = readSavedBaseline();
  updateApplicationDraft((current) => {
    // Indexed writes go through the draft's `unknown` index signature; the named
    // step slots are more specific than what comes back out of JSON.
    const next = { ...current } as Record<string, unknown>;
    for (const key of WIZARD_STEP_DATA_KEYS) {
      const saved = baseline?.[key];
      // Restore a slot that existed at save time; drop one that did not (it is
      // entirely unsaved). No baseline at all = nothing was ever saved, so a
      // full clear is correct and matches the original NP-469 behaviour.
      if (saved !== undefined) next[key] = saved;
      else delete next[key];
    }
    return next as ApplicationDraft;
  });
  // The step-recovery snapshot carries its own copy of the step data, so it is
  // REVERTED to the same baseline as the draft above — not emptied. Emptying it
  // was the NP-469 behaviour and it is what reintroduces this bug: the mount
  // restore would spread a stale `{}` over the values the draft just kept, and
  // the two stores would disagree about what "saved" means. A test pins
  // `formData` to the baseline, so changing it back to `{}` fails.
  //
  // ALL bookkeeping is carried through, not just currentStep. `furthestStep` is
  // read back on mount as `Math.max(0, parsed.furthestStep || parsed.currentStep)`
  // (ApplicationShell Effect 1), so dropping it silently regresses the borrower's
  // frontier to wherever they happened to be standing: save through step 5, go back
  // to step 2, type, discard, reopen /application (Dashboard links carry no ?step=)
  // and step 2 becomes the frontier — throwing away progress earned by SAVED data
  // that this discard explicitly promises to keep. Only `formData` is data.
  try {
    const raw = window.localStorage.getItem('path_application_progress');
    if (!raw) return;
    const parsed = JSON.parse(raw) as {
      currentStep?: number;
      furthestStep?: number;
      lastSaved?: string;
    };
    window.localStorage.setItem(
      'path_application_progress',
      JSON.stringify({
        currentStep: parsed.currentStep ?? 0,
        ...(parsed.furthestStep !== undefined ? { furthestStep: parsed.furthestStep } : {}),
        ...(parsed.lastSaved !== undefined ? { lastSaved: parsed.lastSaved } : {}),
        // NP-463: the recovery snapshot carries its own copy of the step data,
        // so it has to land on the SAME baseline as the draft above — emptying
        // it while the draft keeps saved values would make the two disagree and
        // the mount restore would spread a stale `{}` over the real data.
        formData: baseline ?? {},
      }),
    );
  } catch {
    // Unparseable snapshot — drop it entirely rather than leave stale step data.
    try {
      window.localStorage.removeItem('path_application_progress');
    } catch {
      // storage unavailable; the draft clear above is the important half
    }
  }
}
